import { EXCEPTION_FLOW, EXCEPTION_NUMBER_LABEL } from '../../../common/constants';

const checkIfDefaultExceptionFlow = (exceptionFlow) => {
  return !exceptionFlow || exceptionFlow === EXCEPTION_FLOW.DEFAULT;
};

const checkIfBothExceptionFlow = (exceptionFlow) => {
  return exceptionFlow === EXCEPTION_FLOW.BOTH;
};

const parseExceptionConfig = (exceptionConfig) => {
  if (!exceptionConfig) return null;
  if (typeof exceptionConfig === 'string') {
    try {
      return JSON.parse(exceptionConfig);
    } catch (e) {
      return null;
    }
  }
  return exceptionConfig;
};

// Get exception number label from clientCodes (same source as exceptionList)
// Labels come from GetClientCodes API and are stored in DispatchStore.staticClientData.clientCodes.labels
const getExceptionNumberLabel = (DispatchStore) => {
  // Primary source: GetClientCodes API response stored in DispatchStore
  const label = DispatchStore?.staticClientData?.clientCodes?.labels?.exceptionNumber;
  if (label) {
    // Return whatever label the API provides (e.g., "Exception Number", "EA Number", "ADE Number", etc.)
    if(label === EXCEPTION_NUMBER_LABEL.EA_NUMBER || label === EXCEPTION_NUMBER_LABEL.ADE_NUMBER) {
      return label;
    }
  }
  return EXCEPTION_NUMBER_LABEL.ADE_NUMBER;
};

const hasErrors = (errorInfo) => {
  return Object.values(errorInfo).some((error) => Boolean(error));
};

// This file uses a factory method (createExceptionUtils) to generate customizable exception utility objects for different exception flows.
// Each flow (e.g., DefaultException, ADEException) can define its own validation logic and fields while sharing common utility functionality.
const createExceptionUtils = ({
  validatedFields,
  validateExceptionField,
  getExceptionDetailsObject
}) => {
  const validateExceptionObject = (exceptionObject, isReturn) => {
    return validatedFields.reduce((errorInfo, field) => {
      if (isReturn && field === 'code') {
        errorInfo[field] = null;
      } else {
        errorInfo[field] = validateExceptionField(field, exceptionObject[field]);
      }
      return errorInfo;
    }, {});
  };

  const validateExceptionDetail = (exceptionDetail = [], isReturn = false) => {
    const exceptionsWithErrors = exceptionDetail.map((exception) => ({
      ...exception,
      errorInfo: validateExceptionObject(exception, isReturn)
    }));

    const hasAnyErrors = exceptionsWithErrors.some((exception) => hasErrors(exception.errorInfo));

    return hasAnyErrors ? exceptionsWithErrors : null;
  };

  const getDefaultExceptionDetail = (exceptionDetail = []) => {
    if (!exceptionDetail.length) {
      return [getExceptionDetailsObject()];
    }
    return exceptionDetail.map((exception) => getExceptionDetailsObject(exception));
  };

  const getExceptionRequestPayload = (exceptions = [], isReturn = false) => {
    return exceptions.map((exception) => {
      const { errorInfo, ...exceptionDetail } = exception;
      if (isReturn) {
        const { code, ...rest } = exceptionDetail;
        return rest;
      }
      return exceptionDetail;
    });
  };

  return {
    validateExceptionField,
    validateExceptionDetail,
    getExceptionDetailsObject,
    getDefaultExceptionDetail,
    getExceptionRequestPayload
  };
};

export { checkIfDefaultExceptionFlow, checkIfBothExceptionFlow, hasErrors, createExceptionUtils, parseExceptionConfig, getExceptionNumberLabel };
